import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { AlertTriangle, TrendingDown, Clock, MapPin, ExternalLink, Users, Instagram, Play, Heart, Mail } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import InteractiveTimeline from '../components/InteractiveTimeline';
import BackToTop from '../components/BackToTop';

const Home = () => {
  const [guess, setGuess] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [contactForm, setContactForm] = useState({ email: '', subject: '', message: '' });
  const { toast } = useToast();

  const handleGuess = () => {
    if (!guess) return;
    
    const guessNum = parseInt(guess);
    setShowResult(true);
    
    toast({
      title: "The Reality",
      description: "Scientists estimate fewer than 10 vaquitas remain in the wild. Every individual matters for species survival.",
      duration: 6000,
    });
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!contactForm.email || !contactForm.subject || !contactForm.message) {
      toast({
        title: "Missing Information",
        description: "Please fill in all fields before sending.",
        variant: "destructive",
      });
      return;
    }
    
    try {
      const response = await fetch('https://formspree.io/f/manlodnj', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: contactForm.email,
          subject: contactForm.subject,
          message: contactForm.message,
        }),
      });

      if (response.ok) {
        toast({
          title: "Message Sent! ✓",
          description: "Thank you for reaching out. We'll get back to you soon!",
          duration: 5000,
        });
        
        // Reset form
        setContactForm({ email: '', subject: '', message: '' });
      } else {
        throw new Error('Failed to send');
      }
    } catch (error) {
      // Fallback to mailto if service fails
      const subject = encodeURIComponent(contactForm.subject);
      const body = encodeURIComponent(`From: ${contactForm.email}\n\nMessage:\n${contactForm.message}`);
      const mailtoLink = `mailto:fightforvaquita@gmail.com?subject=${subject}&body=${body}`;
      
      window.open(mailtoLink, '_blank');
      
      toast({
        title: "Opening Email Client",
        description: "Please send the email from your email program.",
        duration: 5000,
      });
    }
  };

  return (
    <div className="bg-gray-900">

      <section className="min-h-screen bg-gray-900 text-white relative overflow-hidden">
        
        {/* Video Background */}
        {
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        >
          <source src="Video/Background.mp4" type="video/mp4" />
        </video>
        }
        
        <div className="absolute inset-0 bg-gray-900/85"></div>
        
        {/* Centered content */}
        <div className="relative z-20 flex items-center justify-center min-h-screen">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center space-x-3 bg-red-700 px-6 py-3 mb-12">
              <AlertTriangle className="h-5 w-5" />
              <span className="text-sm font-semibold uppercase tracking-wider">Extinction Crisis</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold mb-8 leading-tight">
              Fewer Than 10
              <span className="block text-blue-400">Vaquitas Left</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 mb-16 leading-relaxed max-w-4xl mx-auto">
              The world's most endangered marine mammal faces extinction within months. 
              Time is running out for the vaquita porpoise in Mexico's Gulf of California.
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/about">
                <Button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-12 py-4 text-lg">
                  <Play className="mr-3 h-5 w-5" />
                  Learn About Vaquitas
                </Button>
              </Link>
              <Link to="/action">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-gray-900 font-semibold px-12 py-4 text-lg bg-transparent">
                  <Heart className="mr-3 h-5 w-5" />
                  Take Action Now
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Clean Gallery Section - Dark Theme */}
      <section className="py-24 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">
              Meet the Vaquita
            </h2>
            <div className="w-24 h-1 bg-blue-500 mx-auto"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-16">
            {[
              {
                src: "Photos/Vaquita2_Olson_NOAA.jpg",
                title: "Vaquita Swimming",
                subtitle: "Upper Gulf of California"
              },
              {
                src: "Photos/Vaquita_swimming_through_the_void._(Phocoena_Sinus)_2.png",
                title: "Distinctive Features", 
                subtitle: "Dark eye patches & small size"
              },
              {
                src: "https://upload.wikimedia.org/wikipedia/commons/c/cb/Cetacea_range_map_Vaquita.PNG",
                title: "Their Home",
                subtitle: "Gulf of California waters"
              }
            ].map((item, index) => (
              <div key={index} className="group">
                <div className="relative overflow-hidden shadow-xl transition-all duration-500 bg-gray-900 group-hover:shadow-2xl group-hover:shadow-blue-500/20">
                  <img 
                    src={item.src}
                    alt={`${item.title} - Creative Commons`}
                    className="w-full h-80 object-cover transition-all duration-500 group-hover:scale-105 group-hover:brightness-110"
                  />
                  {/* Always visible gradient overlay at bottom */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-gray-900 via-gray-900/80 to-transparent pt-20 pb-6 px-6">
                    <h3 className="font-bold text-xl mb-2 text-white transition-colors duration-300 group-hover:text-blue-400">{item.title}</h3>
                    <p className="text-gray-300 transition-colors duration-300 group-hover:text-white">{item.subtitle}</p>
                  </div>
                </div>
                {index === 2 && (
                  <div className="mt-2 text-xs text-gray-400">
                    <span>
                      Credit:&nbsp;
                      <a href="https://commons.wikimedia.org/wiki/File:Cetacea_range_map_Vaquita.PNG" target="_blank" rel="noopener noreferrer">
                        The original uploader was Pcb21 at English Wikipedia
                      </a>
                      ,&nbsp;
                      <a href="http://creativecommons.org/licenses/by-sa/3.0/" target="_blank" rel="noopener noreferrer">
                        CC BY-SA 3.0
                      </a>
                      , via Wikimedia Commons
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="text-center">
            <p className="text-gray-300 text-lg mb-8 leading-relaxed max-w-3xl mx-auto">
              The vaquita is the world's smallest and most endangered cetacean. 
              These shy porpoises are found nowhere else on Earth.
            </p>
            <Link to="/about">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-10 py-4 text-xl rounded-lg">
                Discover More About Vaquitas
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Interactive Timeline Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              The Vaquita Crisis Timeline
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              From discovery to the brink of extinction. Follow the critical moments in the vaquita's story.
            </p>
          </div>
          
          <InteractiveTimeline />
        </div>
      </section>

      {/* Guess Section with Background Photo */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-purple-900 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: `url('https://upload.wikimedia.org/wikipedia/commons/thumb/4/4a/Ocean_conservation_research.jpg/1200px-Ocean_conservation_research.jpg')`
          }}
        ></div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-8">
            How Many Vaquitas Do You Think Are Left?
          </h2>
          <p className="text-lg md:text-xl text-blue-100 mb-10 leading-relaxed">
            Take a guess, then discover how close we are to losing this species forever.
          </p>
          
          {!showResult ? (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center max-w-md mx-auto">
                <Input
                  type="number"
                  placeholder="Your guess..."
                  value={guess}
                  onChange={(e) => setGuess(e.target.value)}
                  className="text-center text-xl bg-black/90 border-2 border-blue-300 h-14"
                />
                <Button 
                  onClick={handleGuess}
                  className="bg-white text-blue-900 hover:bg-gray-100 font-bold px-10 py-4 text-xl rounded-lg w-full sm:w-auto"
                >
                  Reveal Answer
                </Button>
              </div>
            </div>
          ) : (
            <div className="bg-red-800/80 backdrop-blur border-2 border-red-400 rounded-2xl p-8 max-w-2xl mx-auto">
              <h3 className="text-2xl font-bold text-red-100 mb-4">The Devastating Reality</h3>
              <div className="text-6xl font-bold text-white mb-4">&lt; 10</div>
              <p className="text-red-100 text-lg leading-relaxed">
                Fewer than 10 vaquitas remain in the wild. Your guess was {guess}, but the situation is far more critical than most people realize. Every single vaquita matters for the species' survival.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Simplified Navigation Section - Polar Bears Style */}
      <section className="py-20 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            
            {/* About Card */}
            <Link to="/about" className="group relative overflow-hidden h-96 rounded-lg">
              <img 
                src="Photos/Vaquita2_Olson_NOAA.jpg"
                alt="Learn about vaquitas"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900/95 via-blue-900/60 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <div className="flex items-center gap-3 mb-4">
                  <h3 className="text-2xl md:text-3xl font-bold">About Vaquitas</h3>
                  <div className="bg-yellow-400 text-blue-900 p-2 rounded transform group-hover:translate-x-2 transition-transform">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </div>
                </div>
                <p className="text-gray-200 text-base">
                  Learn about their biology, habitat, and unique characteristics.
                </p>
              </div>
            </Link>

            {/* Threats Card */}
            <Link to="/threats" className="group relative overflow-hidden h-96 rounded-lg">
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/thumb/6/66/Fish_and_Wildlife_Service_worker_on_boat_checking_gill_net_full_of_fish.jpg/1200px-Fish_and_Wildlife_Service_worker_on_boat_checking_gill_net_full_of_fish.jpg?20100917201910"
                alt="Discover the threats"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900/95 via-blue-900/60 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <div className="flex items-center gap-3 mb-4">
                  <h3 className="text-2xl md:text-3xl font-bold">Threats</h3>
                  <div className="bg-yellow-400 text-blue-900 p-2 rounded transform group-hover:translate-x-2 transition-transform">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </div>
                </div>
                <p className="text-gray-200 text-base">
                  Understand what's pushing vaquitas to extinction.
                </p>
              </div>
            </Link>

            {/* Action Card */}
            <Link to="/action" className="group relative overflow-hidden h-96 rounded-lg">
              <img 
                src="https://www.flickr.com/photo_download.gne?id=16043943534&secret=023fd23925&size=h&source=photoPageEngagement"
                alt="Take action"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900/95 via-blue-900/60 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <div className="flex items-center gap-3 mb-4">
                  <h3 className="text-2xl md:text-3xl font-bold">Take Action</h3>
                  <div className="bg-yellow-400 text-blue-900 p-2 rounded transform group-hover:translate-x-2 transition-transform">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </div>
                </div>
                <p className="text-gray-200 text-base">
                  Discover how you can help save the vaquita.
                </p>
              </div>
            </Link>

          </div>
        </div>
      </section>

      {/* Contact Form Section with Frog Background */}
      <section className="py-20 bg-gray-700 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{
            backgroundImage: `url('https://live.staticflickr.com/6151/6167212322_d2728b372c.jpg')`
          }}
        ></div>
        <div className="absolute inset-0 bg-gray-900/60"></div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Come Say Hello. We're Pretty Good Listeners
          </h2>
          <p className="text-lg md:text-xl text-gray-200 mb-10 leading-relaxed">
            Drop us a line and let us know how we can help your cause
          </p>
          
          <form onSubmit={handleContactSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                type="email"
                placeholder="Your Email"
                required
                value={contactForm.email}
                onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                className="text-lg bg-white/95 border-2 border-gray-300 h-14 text-gray-900 placeholder:text-gray-500"
              />
              <Input
                type="text"
                placeholder="Subject"
                required
                value={contactForm.subject}
                onChange={(e) => setContactForm({...contactForm, subject: e.target.value})}
                className="text-lg bg-white/95 border-2 border-gray-300 h-14 text-gray-900 placeholder:text-gray-500"
              />
            </div>
            <textarea
              placeholder="Your Message"
              required
              value={contactForm.message}
              onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
              rows={6}
              className="w-full text-lg bg-white/95 border-2 border-gray-300 rounded-md p-4 text-gray-900 placeholder:text-gray-500"
            />
            <Button 
              type="submit"
              className="bg-black hover:bg-gray-800 text-white font-bold px-12 py-4 text-lg rounded-lg"
            >
              SEND
            </Button>
          </form>
        </div>
      </section>

      {/* Social Media Integration with Photos */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            
            <div>
              <h2 className="text-3xl font-bold text-white mb-6">Stay Connected</h2>
              <p className="text-lg text-gray-300 mb-8">
                Follow our conservation efforts and share the vaquita's story
              </p>
              
              <div className="flex justify-center space-x-6 mb-6">
                <a 
                  href="https://www.instagram.com/fightforvaquita" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white p-4 rounded-full transition-all duration-300 hover:scale-110"
                  aria-label="Follow us on Instagram"
                >
                  <Instagram className="h-6 w-6" />
                </a>
                <a 
                  href="https://x.com/FightforVaquita" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-black hover:bg-gray-800 text-white p-4 rounded-full transition-all duration-300 hover:scale-110"
                  aria-label="Follow us on X"
                >
                  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
                <a 
                  href="https://www.facebook.com/fightforvaquita" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full transition-all duration-300 hover:scale-110"
                  aria-label="Follow us on Facebook"
                >
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
              </div>
              
              <div className="text-gray-300">
                <a 
                  href="mailto:fightforvaquita@gmail.com" 
                  className="inline-flex items-center gap-2 hover:text-blue-400 transition-colors"
                >
                  <Mail className="h-5 w-5" />
                  fightforvaquita@gmail.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <div className="fixed right-4 bottom-4 z-40">
        <BackToTop />
      </div>

    </div>
  );
};

export default Home;